import { useState } from "react";

import { parseExcelFile } from "../utils";
import { validateExcelHeaders, validateFile } from "../utils/validation";

export interface UseExcelUploadOptions {
  expectedHeaders: string[];
  onSuccess?: (data: Record<string, unknown>[], rowCount: number) => void;
  onError?: (error: string) => void;
}

export interface UseExcelUploadReturn {
  isUploading: boolean;
  validationError: string | null;
  validateExcelFile: (file: File) => Promise<void>;
  clearUploadedData: () => void;
}

/**
 * Custom hook for handling Excel file uploads
 * Separates business logic from UI components for better testability
 */
export const useExcelValidationUpload = ({
  expectedHeaders,
  onSuccess,
  onError
}: UseExcelUploadOptions): UseExcelUploadReturn => {
  const [isUploading, setIsUploading] = useState(false);
  const [validationError, setUploadError] = useState<string | null>(null);

  const validateExcelFile = async (file: File): Promise<void> => {
    setIsUploading(true);
    setUploadError(null);

    try {
      // Validate file
      const validation = validateFile(file);
      if (!validation.isValid) {
        const errorMessage = validation.errors.join(". ");
        setUploadError(errorMessage);
        onError?.(errorMessage);
        return;
      }

      // Parse Excel file
      const { data, headers } = await parseExcelFile(file);

      // Validate headers
      const headerValidation = validateExcelHeaders(headers, expectedHeaders);
      if (!headerValidation.isValid) {
        const errorMessage = `Missing columns: ${headerValidation.missingHeaders.join(
          ", "
        )}`;
        setUploadError(errorMessage);
        onError?.(errorMessage);
        return;
      }

      // Success
      setUploadError(null);
      onSuccess?.(data, data.length);
    } catch (error) {
      const errorMessage =
        "Failed to parse Excel file. Please ensure it is a valid Excel file.";
      setUploadError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const clearUploadedData = () => {
    setUploadError(null);
  };

  return {
    isUploading,
    validationError,
    validateExcelFile,
    clearUploadedData
  };
};
